import _ from 'lodash'
import $ from 'jquery'
import Backbone from 'backbone'
import Table from 'scoreboard/reports/common/table/table'
import SingleColumnStack from 'scoreboard/reports/common/table/single-column-stack'
import GroupedSingleColumnStack from 'scoreboard/reports/common/table/grouped-single-column-stack'
import MultiColumnTableStack from 'scoreboard/reports/common/table/multi-column-table-stack'
import AdvancedLayoutStack from 'scoreboard/reports/common/table/advanced-layout-stack'
import arrayToHash from 'scoreboard/components/array-to-hash'
import reportConstants from 'scoreboard/reports/report-constants'

function groupingDetail(grouping, tableStack, dataByGroupIndexKey, outerGroupIndex,
	innerGroupIndex, reportModel) {
	let subgroupIndex = new String(_.isNil(innerGroupIndex) ? 0 : innerGroupIndex)
	//the subgroup must be a fractional to maintain sort order, pad to 6
	//to handle the theoretical max of 100000
	let groupIndexKey = `${outerGroupIndex}.${subgroupIndex.padStart(6, '0')}`
	let formattedValue = tableStack.formatValueForHeader(grouping, reportModel)
	return {
		structure: _.defaults({
			stack: tableStack,
			outerGroupIndex,
			innerGroupIndex,
			truncated: grouping.truncated,
			isSubgroup: function() { return !_.isNil(this.innerGroupIndex) }
		}, formattedValue),
		items: dataByGroupIndexKey[groupIndexKey],
		groupIndexKey
	}
}

function associatedByGroupIndexKey(data, dataByGroupIndexKey = {}) {
	_.each(data, datum => {
		let groupIndexKey = _.get(datum, 'groupIndexKey', reportConstants.DEFAULT_GROUP_INDEX_KEY)
		let dataRow = dataByGroupIndexKey[groupIndexKey] || []
		dataRow.push(datum)
		dataByGroupIndexKey[groupIndexKey] = dataRow
	})
	return dataByGroupIndexKey
}

/**
 * The data model representing the report table for a report
 */
const BaseReportTableModel = Backbone.Model.extend({

	/**
	 * Returns the section drilldowns in the table go to, null if there should
	 * be no drilldowns
	 */
	_drilldownSection: function() {
		return null
	},

	/**
	 * Builds the data model used by the report from the model representing the
	 * scorecard/initiative/dataset report
	 *
	 * @param {Backbone.Model} model The Scorecard/Initiative/Dataset Report Model
	 * @param {DOMNode} tableNode The DOM Node where the table will be set to
	 * @param [Object={}] tableOptions Options to pass to the Table
	 * @param [Array=null] data Array of data to use, otherwise uses the data
	 * found on the model
	 */
	//TODO: refactor big method
	build(model, tableNode, tableOptions = {}, data) {
		this._tableNode = $(tableNode)
		this.model = model
		this._dataExtractor = this.dataExtractor(model)

		//clear previous listeners
		this.stopListening(this.model)

		//handle long loads
		this.listenTo(this.model, 'request', (model, xhr, options) => {
			if (options.persist && !this.model.editing) {
				//#40449 we're solely persisted the data so nothing is returned
				//don't trigger the loading dots
				return
			}
			this._table.loading()
		})

		this.listenTo(this.model, 'error', (model, res, opts) => {
			this._table.render()
		})

		//handle incoming row data
		this.listenTo(this.model, 'data', (data, opts) => {
			if (_.isNil(data) || _.isEmpty(data)) { return }

			let structure = []
			//if append is not specified then append only if we're not editing
			//the model because editing only deals with a small amount of data
			let append = _.isNil(opts.append) ? !this.model.editing : opts.append
			if (append) {
				//partition the data into group sets (if grouping is not enabled, it'll
				//just the default first set)
				let dataByGroupIndexKey = associatedByGroupIndexKey(data)
				_.each(dataByGroupIndexKey, (groupData, groupIndexKey) => {
					structure.push({
						data: groupData,
						//this maps the data to the correct grouping in the table
						groupIndexKey
					})
				})
			} else {
				//if we're not appending, rebuild the table structure since it could
				//have changed (#43959/#43960)
				structure = this._buildTableStructureData(this.model, data)
			}

			//update the table structure with the new data and re-render
			this._table
				.updateTableStructure(structure, {
					append,
					errorMessage: this.model.error()
				})
				.render()

			this.trigger('data', data, opts)
		})

		//handle changes to the stack structure
		this.listenTo(this.model, 'change:structure', (model, value, opts) => {
			let data = this.model.data()
			if (_.isEmpty(data)) {
				//when changing the structure, the data may in the process of
				//being retrieved of re-organized, if that's the case don't show
				//any changes until the data event above (which handles rendering
				//the incoming data change)
				data = null
			}
			this._table
				.updateTableStructure(this._buildTableStructureData(this.model, data), {
					errorMessage: this.model.error()
				})
				.render()
		})

		let noResultsMessage = 'report.customReport.noResultsFilterReason'
		let dataset = this.model.get('dataset')
		// If the model here is a dataset which needs to finish recalculating, show
		// a more relevant message #36994
		if (!_.isNil(dataset) && !dataset.get('complete')) {
			noResultsMessage = 'report.customReport.noResultsNotRecalculatedYet'
		}

		//if we're not in editable mode, allow drilldowns if report supports it
		let drilldownSection = !model.editing ? this._drilldownSection() : null
		let generalTableOptions = _.extend({}, {
			noResultsMessage,
			drilldownSection,
			errorMessage: this.model.error(),
			model: this.model
		}, tableOptions)

		_.invoke(this._table, 'remove')

		let initialData = data || this.model.data()
		this._table = new Table(this._buildTableStructureData(model, initialData),
			tableNode, generalTableOptions).render()

		return this._table
	},

	//TODO: refactor big method
	_buildTableStructureData(model, data) {
		let tableStructure = []
		let stacks = model.stacks()
		if (stacks.length == 0) {
			return tableStructure
		}
		//the data will be formatted by the columns, so use a copy so that this
		//method can be idempotent
		data = _.cloneDeep(data)

		//pass along the grouping info so the data extractors know which stacks
		//we're grouping over. This is a map whose key is the id of the model
		//whose stack data is being grouped over, and the value is the grouping level
		//note because you can group on an unsaved stack, the id is either an
		//integer (saved id) or a backbone cid (unsaved id), the resolution of
		//this is called the "resolved" id
		let groupPositionByGroupingId = arrayToHash(model.attributes.groupBy, (v, k) => {return k})
		//only show aggregation data if we're hiding individual records #20266
		let aggregating = !model.isShowingIndividualRecords()
			&& !_.isEmpty(groupPositionByGroupingId)

		//parse out the stack in the table
		let headerStacks = []
		let stackByGroupingId = {}

		_.each(stacks.models, (stack, stackIndex) => {
			let stackOptions = {
				reportModel: model,
				stack,
				dataExtractor: this._dataExtractor
			}

			//make a copy of each column's attributes within each report stack
			let columnsAttributes = _.map(stack.columns().models, column => {
				//if we're aggregating and we're the stack type grouping on
				//then this stack is aggregated
				let stackAggregated = aggregating && _.isNil(column.get('groupingId'))
				return _.defaults({
						cid: column.cid,
						column,
						stackAggregated
					}, column.clone().attributes)
			})

			let firstColumnAttributes = columnsAttributes[0]
			if (stack.has('repeatBy')) {
				let repeatBy = stack.get('repeatBy').clone()
				headerStacks.push(new MultiColumnTableStack(stackIndex, columnsAttributes,
					repeatBy.attributes, stackOptions))
			} else if (stack.has('advancedLayout')) {
				let cellLayout = stack.get('advancedLayout')
				headerStacks.push(new AdvancedLayoutStack(stackIndex,
					firstColumnAttributes, cellLayout, stackOptions))
			} else {
				let {groupingId} = firstColumnAttributes
				let groupPosition = groupPositionByGroupingId[groupingId]
				let numGroups = _.get(model, 'attributes.groupBy.length', 0)
				let isSecondLevelGrouping = numGroups == 2
				let isAggregatingWithOnlyOneGroup = aggregating && numGroups == 1
				let isAggregatingWithInnerGroup = aggregating && isSecondLevelGrouping && groupPosition == 1

				//the table stack header shows if ONE is true
				//1. the stack is not being grouped on
				//2. the stack is grouped and is aggregating and there is only one grouping
				//3. the stack is grouped and is aggregating and is second level grouping
				//   in the second position (inner group)
				//   (NOTE #40405 this was first originally but switch to second due to
				//   to the reordering of the grouping in #40325)
				if (_.isNil(groupPosition) || isAggregatingWithOnlyOneGroup || isAggregatingWithInnerGroup) {
					headerStacks.push(new SingleColumnStack(stackIndex, firstColumnAttributes, stackOptions))
				} else {
					stackByGroupingId[groupingId] = new GroupedSingleColumnStack(stackIndex,
						firstColumnAttributes, stackOptions)
				}
			}
		})

		//setup the table structure
		let groupData = model.groupings()

		//table has grouped header only if, there is grouping data AND a grouped
		//header stack was found.  Normally anytime we're grouped we have a grouped
		//header, the exception is when we are only grouping by one level AND we
		//are hiding individual records (i.e. aggregating) in that situation even
		//though we are grouping, because we are aggregated AND are not grouping
		//more than one level we don't show a grouped header.  This logic to determine
		//this is handled above when parsing the stack structure
		let hasGroupedHeaders = !!(groupData.length && !_.isEmpty(stackByGroupingId))
		if (hasGroupedHeaders) {
			let groupDetails = this._compileGroupingDetails(model, data, stackByGroupingId)
			_.each(groupDetails, details => {
				tableStructure.push({
					grouping: details.structure,
					header: headerStacks,
					data: details.items,
					groupIndexKey: details.groupIndexKey,
					aggregating
				})
			})
		} else {
			//we're not grouping or we're aggregating (i.e. grouping and hiding individual records)
			//in that case we're just working with a single data set
			tableStructure.push({
				header: headerStacks,
				data,
				aggregating		//true if we're aggregating
			})
		}

		this._tableNode.closest('.panel').toggleClass('has-grouping-table', hasGroupedHeaders)
		return tableStructure
	},

	_compileGroupingDetails(model, rawData, stackByGroupingId) {
		let groupData = model.groupings()
		let dataByGroupIndexKey = associatedByGroupIndexKey(rawData)

		//flatten the group data for the table model
		let details = []
		_.each(groupData, (outerGrouping, outerGroupIndex) => {
			let outerTableStack = stackByGroupingId[outerGrouping.groupingId]
			let outerGroupDetail = groupingDetail(outerGrouping, outerTableStack,
				dataByGroupIndexKey, outerGroupIndex, null, this.model)

			let hasInnerGrouping = !_.isEmpty(outerGrouping.innerGrouping)
			if (hasInnerGrouping) {
				_.each(outerGrouping.innerGrouping, (innerGrouping, innerGroupIndex) => {
					let innerTableStack = stackByGroupingId[innerGrouping.groupingId]
					let groupDetail = groupingDetail(innerGrouping, innerTableStack,
						dataByGroupIndexKey, outerGroupIndex, innerGroupIndex, model)
					//inner group keeps reference to outer group
					groupDetail.structure.outerGrouping = outerGroupDetail.structure
					details.push(groupDetail)
				})
			} else {
				details.push(outerGroupDetail)
			}
		})

		return details
	},

	/**
	 * Returns true if the table is able to be edited
	 */
	isEditable: function() {
		return _.get(this._table, 'editable', false)
	},

	applySpotlight() {
		this._table.applySpotlight()
	},

	dataExtractor: function(model) {
		throw Error('Subclass must implement')
	},

	remove: function() {
		this.stopListening(this.model)
	}

})

export default BaseReportTableModel
BaseReportTableModel.__filename__ = 'reports/common/base-report-table-model.js'
